import { createWebI18n } from '@bravemove/i18n/web';
import enAdmin from './locales/en/admin.json';
import enVenue from './locales/en/venue.json';
import enClaim from './locales/en/claim.json';
import enVenueEditor from './locales/en/venue-editor.json';
import enStaff from './locales/en/staff.json';
import enAffiliation from './locales/en/affiliation.json';
import enAuditLog from './locales/en/audit-log.json';
import enCommission from './locales/en/commission.json';
import enWhatsApp from './locales/en/whatsapp.json';
// F29 — admin-specific team moderation copy (queue title, suspend/reinstate/
// force-disband dialog copy, audit log title). Shared `team` namespace covers
// role labels + audit action labels + error codes; admin-specific dialog +
// page titles live here.
import enTeam from './locales/en/team.json';
// F31 (Wave 2B) — admin-specific skill copy: skill-disputes queue/detail,
// sport-skill-descriptors CMS, per-user skill-ratings sub-tab + override
// dialog. Shared `skill` namespace (@bravemove/i18n) covers status / action /
// result / error / dialog labels; access those via `t('skill:...')` and the
// admin-specific copy via `t('admin:skill.disputes.page_title')`.
import enSkill from './locales/en/skill.json';
// F32 (Wave 2C) — admin-specific open-games nav labels. The shared `open-game`
// namespace (@bravemove/i18n) covers status / report-reason / admin list+detail+
// dialog + error labels; access those via `t('open-game:...')` and the
// admin-specific sidebar copy via `t('admin:open-game.nav.openGames')`.
import enOpenGame from './locales/en/open-game.json';
// F36 (Wave 2E) — admin-specific club moderation copy (queue, detail tabs,
// verify/suspend/reinstate dialogs). The shared `club` namespace (@bravemove/i18n)
// covers role / status / audit-action / error labels; access those via
// `t('club:...')` and the admin moderation copy via
// `t('admin:club.queue.title')` etc.
import enClub from './locales/en/club.json';
// F37 (Wave 2D) — admin-specific recommendation copy (nav labels, catalog CMS
// page + dialog copy, moderation queue + detail + action-dialog copy). The
// shared `recommendation` namespace (@bravemove/i18n) covers status / action /
// error / catalog field labels; access those via `t('recommendation:...')` and
// the admin-specific copy via `t('admin:recommendation.nav.moderation')` etc.
import enRecommendation from './locales/en/recommendation.json';
// F38 (Wave 2 Track D) — admin-specific subscription copy (nav labels, tier-
// catalog + feature-matrix page, venue-subscriptions list + detail, the four
// action dialogs, history-event labels, toasts). Tier/status/cadence labels +
// invoice copy live here under the `admin` namespace; access via
// `t('admin:subscription.catalog.title')` etc. Subscription invoice SOURCE
// labels reuse the shared `commission` namespace (`t('commission:source.*')`).
import enSubscription from './locales/en/subscription.json';
// F39 (Wave 2C) — admin-specific featured-placements nav labels. The shared
// `placement` namespace (@bravemove/i18n) covers type / scope / status / calculator
// / badge / error labels + the `admin.*` CMS / approvals / monitor / analytics
// page copy; access those via `t('placement:admin.inventory.title')`. The
// admin sidebar labels live here under the `admin` namespace, accessed via
// `t('admin:placement.nav.inventory')`.
import enPlacement from './locales/en/placement.json';
// F40 (Wave 2 Track D — admin) — admin-specific promotions copy (nav labels,
// moderation list, detail + suspend/restore dialogs, analytics page). The
// shared `promotion` namespace (@bravemove/i18n) covers status / displayMode /
// discountType labels + `admin.*` page copy; access those via
// `t('promotion:status.active')` and the admin sidebar + page copy via
// `t('admin:promotion.nav.promotions')` etc.
import enPromotion from './locales/en/promotion.json';
// F41 (Wave 2 Track D — admin) — admin-specific tournament moderation copy (nav
// labels, moderation list, detail + suspend/unsuspend/edit/cancel/force-refund
// dialogs, organizers approval surface, analytics page). The shared `tournament`
// namespace (@bravemove/i18n) covers status / format / organizer-status / error
// labels; access those via `t('tournament:status.live')` and the admin sidebar +
// page copy via `t('admin:tournament.nav.tournaments')` etc.
import enTournament from './locales/en/tournament.json';
// F44 (Wave 2D) — admin-specific financial dashboard copy (nav labels, KPI
// labels, charts, settlements/refunds/exports/alert-rules page + dialog copy).
// Money labels read amounts via <CurrencyDisplay>; this namespace carries only
// the page chrome + filter/column labels. Access via `t('admin:financial.*')`.
import enFinancial from './locales/en/financial.json';
// F42 — merge wizard / bulk panel / suspend drawer / venue-scoped claims tab
// copy (was orphaned — V-09). Accessed via t('admin:venueMerge.*').
import enAdminVenueMerge from './locales/en/admin-venue-merge.json';
// F60 — Logs & API health pages (/ops/logs, /ops/health). Access via
// `t('admin:opsLogs.*')`.
import enOpsLogs from './locales/en/opsLogs.json';

export const i18nPromise = createWebI18n({
  appNamespace: 'admin',
  // Per-app namespaces are nested under the 'admin' namespace so that
  // t('admin:venue.table.empty') and t('admin:claim.queue.title') resolve.
  appResources: {
    en: {
      admin: {
        ...enAdmin,
        venue: enVenue,
        claim: enClaim,
        'venue-editor': enVenueEditor,
        staff: enStaff,
        affiliation: enAffiliation,
        'audit-log': enAuditLog,
        // F23 (Wave 2C) — admin-specific commission copy. Shared commission
        // namespace (status labels, dialog copy, error codes) lives in
        // @bravemove/i18n; admin-specific page + sidebar + table copy lives here.
        // Access via `t('admin:commission.page.rates.title')` for admin keys
        // and `t('commission:status.draft')` for shared keys.
        commission: enCommission,
        // F27 (Wave 2E) — admin-specific WhatsApp tooling copy. Surfaces:
        // /whatsapp/templates page + /whatsapp/quality page + admin home
        // cost-summary card + per-booking delivery-log panel. Access via
        // `t('admin:whatsapp.templates.page_title')` etc.
        whatsapp: enWhatsApp,
        // F29 — Wave 2C fills the admin moderation surface (table, detail,
        // dialogs); the per-app namespace holds admin-specific copy.
        team: enTeam,
        // F31 — admin skill governance surface (disputes queue/detail,
        // descriptors CMS, per-user ratings + override).
        skill: enSkill,
        // F32 — admin open-games moderation nav labels.
        'open-game': enOpenGame,
        // F36 — admin club moderation surface (queue, detail tabs, verify/
        // suspend/reinstate dialogs).
        club: enClub,
        // F37 — admin recommendation surface (catalog CMS + moderation queue).
        recommendation: enRecommendation,
        // F38 — admin subscription surface (tier catalog + venue subscriptions).
        subscription: enSubscription,
        // F39 — admin featured-placements nav labels (sidebar).
        placement: enPlacement,
        // F40 — admin promotions moderation surface (list + detail + analytics).
        promotion: enPromotion,
        // F41 — admin tournament moderation surface (list + detail + organizers
        // approval + analytics).
        tournament: enTournament,
        // F44 — admin financial dashboard (KPIs + charts + settlements + refunds
        // + exports + alert rules). Access via `t('admin:financial.*')`.
        // MERGE (not replace): admin.json's own top-level `financial` block
        // carries the F06 currency-editor copy (`currentRate.*` + `form.*`);
        // a plain object assignment clobbered it and the /financial page
        // rendered ~18 raw i18n keys. Later keys (F44) win on collisions
        // (none exist today).
        financial: {
          ...((enAdmin as Record<string, unknown>).financial as Record<string, unknown>),
          ...enFinancial,
        },
        // F42 (V-09) — merge wizard / bulk panel / suspend drawer copy.
        venueMerge: enAdminVenueMerge,
        // F60 — Logs & API health.
        opsLogs: enOpsLogs,
      },
    },
  },
});
